import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  useNavigation,
  useNavigationState,
  useRoute,
  type NavigationProp,
  type RouteProp,
} from "@react-navigation/native";
import type { ApolloQueryResult } from "@apollo/client";
import {
  GetCompanionChatByIdDocument,
  GetCompanionChatsForUserPreviewDocument,
  type GetCompanionChatByIdQuery,
  type GetCompanionChatsForUserPreviewQuery,
  useDeleteCompanionChatMutation,
  useGetCompanionChatByIdQuery,
  useGetCompanionChatsForUserPreviewQuery,
  useGetCompanionWorkspaceChatsLazyQuery,
  useGetGeneralCompanionChatsForUserLazyQuery,
  useUpdateCompanionChatMutation,
} from "@axio/graphql/generated/graphql";
import { formatTimeAgo } from "../utils/date";
import {
  useWorkspaceLists,
  GENERAL_WORKSPACE_ID,
  TODO_WORKSPACE_ID,
  LEARNING_ACTIVITIES_WORKSPACE_ID,
  SETTINGS_WORKSPACE_ID,
  HOME_ID,
} from "../hooks/useWorkspaceLists";
import { useResolveChatWorkspace } from "../hooks/useResolveChatWorkspace";
import type { WorkspaceThreadItem } from "../components/chat-companion/types";
import type { RootStackParamList } from "../navigation/navigation.types";
import type { InitialChatMessage } from "../containers/companion/companionMessage.types";
import useResponsiveScreen from "../hooks/useResponsiveScreen";

type SidebarBreakpoint = "mobile" | "tablet" | "desktop";

const getSidebarBreakpoint = (width: number): SidebarBreakpoint => {
  if (width <= 768) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
};

const NAV_WORKSPACES: Record<string, string> = {
  [HOME_ID]: "Companion Home",
  [GENERAL_WORKSPACE_ID]: "Companion Chat",
  [TODO_WORKSPACE_ID]: "To do",
  [LEARNING_ACTIVITIES_WORKSPACE_ID]: "Learning Activities",
  [SETTINGS_WORKSPACE_ID]: "Chat Settings",
};

const ROUTE_TO_WORKSPACE: Record<string, string> = Object.entries(NAV_WORKSPACES).reduce(
  (acc, [workspaceId, routeName]) => {
    acc[routeName] = workspaceId;
    return acc;
  },
  {} as Record<string, string>
);

const SIDEBAR_PREVIEW_LIMIT = 5;
const SIDEBAR_PAGE_SIZE = 10;

type PreviewResult = GetCompanionChatsForUserPreviewQuery["getCompanionChatsForUserPreview"];
type CompanionChatSummary = PreviewResult["data"]["generalChats"][number];

type ThreadState = { chats: CompanionChatSummary[]; totalCount: number };

type ErrorState = {
  title: string;
  message: string;
  onRetry?: () => void;
  retryButtonText?: string;
  onClose?: () => void;
} | null;

type CompanionThreadsContextValue = {
  activeThreadId: string | null;
  setActiveThreadId: (id: string | null) => void;
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: (id: string | null) => void;
  openWorkspaceId: string | null;
  setOpenWorkspaceId: (id: string | null) => void;
  toggleWorkspaceDrawer: (workspaceId: string) => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (value: boolean) => void;
  generalWorkspaces: ReturnType<typeof useWorkspaceLists>["generalWorkspaces"];
  courseWorkspaces: ReturnType<typeof useWorkspaceLists>["courseWorkspaceItems"];
  todoWorkspaces: ReturnType<typeof useWorkspaceLists>["todoWorkspaces"];
  learningActivitiesWorkspaces: ReturnType<typeof useWorkspaceLists>["learningActivitiesWorkspaces"];
  settingsWorkspaces: ReturnType<typeof useWorkspaceLists>["settingsWorkspaces"];
  threadsByWorkspaceId: Record<
    string,
    { threads: WorkspaceThreadItem[]; totalCount: number; hasMore: boolean; isLoadingMore: boolean }
  >;
  activeChatThread: NonNullable<GetCompanionChatByIdQuery["getCompanionChatById"]>["data"] | null;
  activeChatThreadLoading: boolean;
  isLoadingCourseWorkspaces: boolean;
  previewError: string | undefined;
  refetchPreviewChats: () => Promise<ApolloQueryResult<GetCompanionChatsForUserPreviewQuery>>;
  activeChatError: string | undefined;
  refetchActiveChat: () => Promise<ApolloQueryResult<GetCompanionChatByIdQuery>>;
  onWorkspacePress: (id: string) => void;
  onThreadSelect: (id: string) => void;
  onThreadDelete: (id: string) => Promise<void>;
  onNewThread: (workspaceId: string) => void;
  onThreadRename: (id: string, newName: string) => Promise<void>;
  onThreadLoadMore: (workspaceId: string) => Promise<void>;
  applyStreamChatRename: (chatId: string, name: string) => void;
  getThreadName: (threadId: string | null) => string;
  getWorkspaceName: (workspaceId: string | null) => string | undefined;
  error: ErrorState;
  showError: (error: ErrorState) => void;
  hideError: () => void;
};

const CompanionThreadsContext = createContext<CompanionThreadsContextValue | null>(null);

export const CompanionThreadsProvider = ({ children }: { children: ReactNode }) => {
  const { width } = useResponsiveScreen();
  const route = useRoute<RouteProp<RootStackParamList>>();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const navigationState = useNavigationState((state) => state);
  const routeParams = route.params as { chatId?: string; initialMessage?: InitialChatMessage } | undefined;
  const { chatId: routeChatId } = routeParams || {};

  const currentRouteName = navigationState?.routes[navigationState.index]?.name;
  const {
    data: previewData,
    loading: isLoadingCourseWorkspaces,
    refetch: refetchPreviewChats,
  } = useGetCompanionChatsForUserPreviewQuery({
    variables: { limit: SIDEBAR_PREVIEW_LIMIT },
  });
  const previewResult = previewData?.getCompanionChatsForUserPreview;
  const previewError =
    previewResult?.success === false ? (previewResult?.message ?? "Failed to load thread list") : undefined;
  const [fetchWorkspaceChats] = useGetCompanionWorkspaceChatsLazyQuery();
  const [fetchGeneralChats] = useGetGeneralCompanionChatsForUserLazyQuery();
  const [deleteCompanionChat] = useDeleteCompanionChatMutation({
    refetchQueries: [{ query: GetCompanionChatsForUserPreviewDocument, variables: { limit: SIDEBAR_PREVIEW_LIMIT } }],
  });
  const [updateCompanionChat] = useUpdateCompanionChatMutation({
    refetchQueries: [
      { query: GetCompanionChatsForUserPreviewDocument, variables: { limit: SIDEBAR_PREVIEW_LIMIT } },
      { query: GetCompanionChatByIdDocument },
    ],
  });
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [openWorkspaceId, setOpenWorkspaceId] = useState<string | null>(null);
  const [error, setError] = useState<ErrorState>(null);
  const hasInitializedRef = useRef(false);
  const initialBreakpoint = getSidebarBreakpoint(width);
  const [isSidebarCollapsed, setIsSidebarCollapsedState] = useState(initialBreakpoint !== "desktop");
  const desktopCollapsedRef = useRef(false);
  const breakpointRef = useRef<SidebarBreakpoint>(initialBreakpoint);

  const [threadStateByWorkspaceId, setThreadStateByWorkspaceId] = useState<Record<string, ThreadState>>({});
  const [loadingThreadsByWorkspaceId, setLoadingThreadsByWorkspaceId] = useState<Record<string, boolean>>({});
  const loadMoreThreadsRef = useRef<((workspaceId: string) => Promise<void>) | null>(null);

  const effectiveThreadId = activeThreadId ?? routeChatId ?? null;
  const {
    data: activeChatThreadData,
    loading: activeChatThreadLoading,
    refetch: refetchActiveChat,
  } = useGetCompanionChatByIdQuery({
    skip: !effectiveThreadId,
    variables: {
      id: effectiveThreadId!,
    },
  });
  const activeChatResult = activeChatThreadData?.getCompanionChatById as
    | (GetCompanionChatByIdQuery["getCompanionChatById"] & {
        success?: boolean;
        message?: string | null;
        data?: GetCompanionChatByIdQuery["getCompanionChatById"] | null;
      })
    | undefined;
  const activeChatError =
    activeChatResult?.success === false ? (activeChatResult?.message ?? "Failed to load chat") : undefined;
  type ActiveChatPayload = NonNullable<GetCompanionChatByIdQuery["getCompanionChatById"]>["data"];
  const activeChatThread = (activeChatResult?.data ??
    (activeChatResult && "workspacesHasChat" in activeChatResult
      ? activeChatResult
      : null)) as ActiveChatPayload | null;

  const setIsSidebarCollapsed = useCallback(
    (value: boolean) => {
      setIsSidebarCollapsedState(value);
      if (getSidebarBreakpoint(width) !== "mobile") {
        desktopCollapsedRef.current = value;
      }
    },
    [width]
  );

  const toggleWorkspaceDrawer = useCallback(
    (workspaceId: string) => {
      if (openWorkspaceId === workspaceId) {
        setOpenWorkspaceId(null);
      } else {
        setOpenWorkspaceId(workspaceId);
      }
    },
    [openWorkspaceId]
  );

  const mergeThreadState = useCallback(
    (previous: ThreadState | undefined, nextChats: CompanionChatSummary[], totalCount: number) => {
      if (!previous) {
        return { chats: nextChats, totalCount };
      }
      const nextIds = new Set(nextChats.map((chat) => chat.id));
      const preserved = previous.chats.filter((chat) => !nextIds.has(chat.id));
      const merged = [...nextChats, ...preserved];

      // Reconcile with totalCount: if totalCount decreased, remove preserved chats
      // that are no longer returned by the server until we match the new totalCount
      if (merged.length > totalCount) {
        // Keep the new chats first, then trim preserved chats to match totalCount
        const trimmed = merged.slice(0, totalCount);
        return { chats: trimmed, totalCount };
      }

      return { chats: merged, totalCount };
    },
    []
  );

  useEffect(() => {
    const previewPayload = previewResult?.data;
    if (!previewPayload) {
      return;
    }
    const {
      generalChats: nextGeneralChats,
      generalChatsCount: nextGeneralCount,
      courseWorkspaces: nextWorkspaces,
    } = previewPayload;

    setThreadStateByWorkspaceId((prev) => {
      const next = { ...prev };
      next[GENERAL_WORKSPACE_ID] = mergeThreadState(prev[GENERAL_WORKSPACE_ID], nextGeneralChats, nextGeneralCount);
      nextWorkspaces.forEach((workspace) => {
        next[workspace.id] = mergeThreadState(prev[workspace.id], workspace.chats ?? [], workspace.totalCount);
      });
      return next;
    });
  }, [previewResult, mergeThreadState]);

  const previewPayload = previewResult?.data;
  const generalChats = useMemo(() => previewPayload?.generalChats ?? [], [previewPayload]);
  const generalChatsCount = previewPayload?.generalChatsCount ?? generalChats.length;
  const courseWorkspaces = useMemo(() => previewPayload?.courseWorkspaces ?? [], [previewPayload]);
  const { generalWorkspaces, courseWorkspaceItems, todoWorkspaces, learningActivitiesWorkspaces, settingsWorkspaces } =
    useWorkspaceLists({
      generalChats,
      generalChatsCount,
      courseWorkspaces,
      activeWorkspaceId,
    });

  useEffect(() => {
    const nextBreakpoint = getSidebarBreakpoint(width);
    if (nextBreakpoint === breakpointRef.current) {
      return;
    }
    breakpointRef.current = nextBreakpoint;
    if (nextBreakpoint === "desktop") {
      setIsSidebarCollapsedState(desktopCollapsedRef.current);
      return;
    }
    setIsSidebarCollapsedState(true);
  }, [width]);

  const threadsByWorkspaceId = useMemo(() => {
    const out: Record<
      string,
      { threads: WorkspaceThreadItem[]; totalCount: number; hasMore: boolean; isLoadingMore: boolean }
    > = {};

    const getThreadSortTime = (created?: string | null, lastModified?: string | null) => {
      if (created) {
        return new Date(created).getTime();
      }
      if (lastModified) {
        return new Date(lastModified).getTime();
      }
      return Number.POSITIVE_INFINITY;
    };

    Object.entries(threadStateByWorkspaceId).forEach(([workspaceId, state]) => {
      const totalCount = state.totalCount ?? state.chats.length;
      const isGeneral = workspaceId === GENERAL_WORKSPACE_ID;
      const chats = state.chats ?? [];

      const threads = isGeneral
        ? chats.map((chat) => ({
            id: chat.id,
            title: chat.name || "Untitled Thread",
            metaLabel: chat.last_modified ? formatTimeAgo(chat.last_modified) : "",
            isActive: activeThreadId === chat.id,
          }))
        : (() => {
            if (chats.length === 0) {
              return [];
            }
            const mainThreadId = chats.reduce((oldestChat, chat) => {
              const oldestTime = getThreadSortTime(oldestChat.created, oldestChat.last_modified);
              const chatTime = getThreadSortTime(chat.created, chat.last_modified);
              return chatTime < oldestTime ? chat : oldestChat;
            }, chats[0]).id;

            return chats.map((chat) => {
              const isMainThread = chat.id === mainThreadId;
              return {
                id: chat.id,
                title: chat.name || (isMainThread ? "Main Thread" : "Untitled Thread"),
                metaLabel: chat.last_modified ? formatTimeAgo(chat.last_modified) : "",
                isActive: activeThreadId === chat.id,
                isMainThread,
              };
            });
          })();

      out[workspaceId] = {
        threads,
        totalCount,
        hasMore: threads.length < totalCount,
        isLoadingMore: loadingThreadsByWorkspaceId[workspaceId] ?? false,
      };
    });

    return out;
  }, [threadStateByWorkspaceId, activeThreadId, loadingThreadsByWorkspaceId]);

  const handleRefetchPreviewChats = useCallback(() => refetchPreviewChats(), [refetchPreviewChats]);

  const applyStreamChatRename = useCallback((chatId: string, name: string) => {
    setThreadStateByWorkspaceId((prev) => {
      const next = { ...prev };
      Object.entries(next).forEach(([workspaceId, state]) => {
        const updatedChats = state.chats.map((chat) => (chat.id === chatId ? { ...chat, name } : chat));
        next[workspaceId] = { ...state, chats: updatedChats };
      });
      return next;
    });
  }, []);

  const getThreadName = useCallback(
    (threadId: string | null): string => {
      if (!threadId) return "";

      // Search through all workspace threads
      for (const [workspaceId, threadData] of Object.entries(threadsByWorkspaceId)) {
        const thread = threadData.threads.find((t) => t.id === threadId);
        if (thread) {
          return thread.title;
        }
      }

      return "";
    },
    [threadsByWorkspaceId]
  );

  const getWorkspaceName = useCallback(
    (workspaceId: string | null): string | undefined => {
      if (!workspaceId || workspaceId === GENERAL_WORKSPACE_ID) {
        return "General Chat";
      }

      // Search in course workspaces
      const workspace = courseWorkspaceItems.find((w) => w.id === workspaceId);
      return workspace?.title;
    },
    [courseWorkspaceItems]
  );

  const showError = useCallback((errorState: ErrorState) => {
    setError(errorState);
  }, []);

  const hideError = useCallback(() => {
    setError(null);
  }, []);

  const handleWorkspacePress = useCallback(
    (id: string) => {
      setActiveWorkspaceId(id);

      let latestThreadId: string | null = null;
      if (id === GENERAL_WORKSPACE_ID) {
        latestThreadId = generalChats[0]?.id || null;
      } else {
        const workspace = courseWorkspaces.find((w) => w.id === id);
        latestThreadId = workspace?.chats?.[0]?.id || null;
      }

      const routeName = NAV_WORKSPACES[id];

      if (latestThreadId) {
        setActiveThreadId(latestThreadId);
        navigation.navigate("Companion Chat", { chatId: latestThreadId });
      } else if (routeName) {
        navigation.navigate(routeName as never);
      } else {
        setActiveThreadId(null);
        navigation.navigate("Companion Chat", { chatId: undefined, initialMessage: { workspaceId: id, text: "" } });
      }
    },
    [courseWorkspaces, generalChats, navigation, setActiveThreadId, setActiveWorkspaceId]
  );

  const handleSelectThread = useCallback(
    (id: string) => {
      const workspaceIdForAccordion = openWorkspaceId || activeWorkspaceId;
      if (workspaceIdForAccordion) {
        setActiveWorkspaceId(workspaceIdForAccordion);
      }
      setActiveThreadId(id);
      navigation.navigate("Companion Chat", { chatId: id });
    },
    [openWorkspaceId, activeWorkspaceId, navigation, setActiveThreadId, setActiveWorkspaceId]
  );

  const handleRenameThread = useCallback(
    async (id: string, newName: string) => {
      try {
        const result = await updateCompanionChat({ variables: { id, name: newName } });
        if (result.data?.updateCompanionChat?.success) {
          setThreadStateByWorkspaceId((prev) => {
            const next = { ...prev };
            Object.entries(next).forEach(([workspaceId, state]) => {
              const updatedChats = state.chats.map((chat) => (chat.id === id ? { ...chat, name: newName } : chat));
              next[workspaceId] = { ...state, chats: updatedChats };
            });
            return next;
          });
          try {
            await refetchPreviewChats();
          } catch (refetchErr) {
            const refetchMessage =
              refetchErr instanceof Error ? refetchErr.message : "Failed to refresh the thread list.";
            setError({
              title: "Failed to refresh thread list",
              message: refetchMessage || "The thread was renamed but the list could not be updated. Please try again.",
              onRetry: async () => {
                await refetchPreviewChats();
              },
              retryButtonText: "Retry",
            });
            return;
          }
          if (activeThreadId === id) {
            setActiveThreadId(id);
            navigation.setParams({ chatId: id });
          }
        } else {
          throw new Error("Failed to rename thread");
        }
      } catch (error) {
        console.error("Error renaming companion chat:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to rename thread";
        const capturedId = id;
        const capturedName = newName;
        setError({
          title: "Failed to rename thread",
          message: errorMessage || "An error occurred while renaming the thread. Please try again.",
          onRetry: async () => {
            await handleRenameThread(capturedId, capturedName);
          },
          retryButtonText: "Retry",
        });
      }
    },
    [activeThreadId, navigation, refetchPreviewChats, setActiveThreadId, updateCompanionChat]
  );

  const handleNewThread = useCallback(
    (workspaceId: string) => {
      setActiveWorkspaceId(workspaceId);
      setActiveThreadId(null);
      navigation.navigate("Companion Chat", { chatId: undefined });
    },
    [navigation, setActiveThreadId, setActiveWorkspaceId]
  );

  const handleLoadMoreThreads = useCallback(
    async (workspaceId: string) => {
      const currentState = threadStateByWorkspaceId[workspaceId];
      const currentOffset = currentState?.chats.length ?? 0;

      if (currentState && currentState.chats.length >= currentState.totalCount) {
        return;
      }
      if (loadingThreadsByWorkspaceId[workspaceId]) {
        return;
      }
      setLoadingThreadsByWorkspaceId((prev) => ({ ...prev, [workspaceId]: true }));

      try {
        if (workspaceId === GENERAL_WORKSPACE_ID) {
          const result = await fetchGeneralChats({
            variables: {
              offset: currentOffset,
              limit: SIDEBAR_PAGE_SIZE,
            },
          });
          const response = result.data?.getGeneralCompanionChatsForUser;
          if (response) {
            setThreadStateByWorkspaceId((prev) => {
              const existing = prev[workspaceId];
              if (!existing) {
                return {
                  ...prev,
                  [workspaceId]: {
                    chats: response.chats,
                    totalCount: response.totalCount,
                  },
                };
              }
              const existingIds = new Set(existing.chats.map((chat) => chat.id));
              const newChats = response.chats.filter((chat) => !existingIds.has(chat.id));
              return {
                ...prev,
                [workspaceId]: {
                  chats: [...existing.chats, ...newChats],
                  totalCount: response.totalCount,
                },
              };
            });
          }
          return;
        }

        const result = await fetchWorkspaceChats({
          variables: {
            workspaceId,
            offset: currentOffset,
            limit: SIDEBAR_PAGE_SIZE,
          },
        });
        const response = result.data?.getCompanionWorkspaceChats;
        if (response) {
          setThreadStateByWorkspaceId((prev) => {
            const existing = prev[workspaceId];
            if (!existing) {
              return {
                ...prev,
                [workspaceId]: {
                  chats: response.chats,
                  totalCount: response.totalCount,
                },
              };
            }
            const existingIds = new Set(existing.chats.map((chat) => chat.id));
            const newChats = response.chats.filter((chat) => !existingIds.has(chat.id));
            return {
              ...prev,
              [workspaceId]: {
                chats: [...existing.chats, ...newChats],
                totalCount: response.totalCount,
              },
            };
          });
        }
      } catch (error) {
        console.error("Error loading more threads:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to load more threads";
        const capturedWorkspaceId = workspaceId;
        setError({
          title: "Failed to load threads",
          message: errorMessage || "An error occurred while loading threads. Please try again.",
          onRetry: async () => {
            if (loadMoreThreadsRef.current) {
              await loadMoreThreadsRef.current(capturedWorkspaceId);
            }
          },
          retryButtonText: "Retry",
        });
      } finally {
        setLoadingThreadsByWorkspaceId((prev) => ({ ...prev, [workspaceId]: false }));
      }
    },
    [fetchGeneralChats, fetchWorkspaceChats, loadingThreadsByWorkspaceId, threadStateByWorkspaceId]
  );

  loadMoreThreadsRef.current = handleLoadMoreThreads;

  const handleDeleteThread = useCallback(
    async (id: string) => {
      try {
        const wasActiveThread = activeThreadId === id;
        const result = await deleteCompanionChat({ variables: { id } });
        if (result.data?.deleteCompanionChat?.success) {
          setThreadStateByWorkspaceId((prev) => {
            const next = { ...prev };
            Object.entries(next).forEach(([workspaceId, state]) => {
              const filteredChats = state.chats.filter((chat) => chat.id !== id);
              const removed = filteredChats.length !== state.chats.length;
              next[workspaceId] = {
                chats: filteredChats,
                totalCount: removed ? Math.max(state.totalCount - 1, 0) : state.totalCount,
              };
            });
            return next;
          });
          let refetchResult: ApolloQueryResult<GetCompanionChatsForUserPreviewQuery>;
          try {
            refetchResult = await refetchPreviewChats();
          } catch (refetchErr) {
            const refetchMessage =
              refetchErr instanceof Error ? refetchErr.message : "Failed to refresh the thread list.";
            setError({
              title: "Failed to refresh thread list",
              message: refetchMessage || "The thread was deleted but the list could not be updated. Please try again.",
              onRetry: async () => {
                await refetchPreviewChats();
              },
              retryButtonText: "Retry",
            });
            return;
          }
          if (wasActiveThread) {
            const workspaceId = activeWorkspaceId || GENERAL_WORKSPACE_ID;
            let latestThreadId: string | null = null;
            const refetchPreview = refetchResult.data?.getCompanionChatsForUserPreview;
            if (workspaceId === GENERAL_WORKSPACE_ID) {
              const updatedGeneralChats = refetchPreview?.data?.generalChats ?? [];
              latestThreadId = updatedGeneralChats[0]?.id || null;
            } else {
              const updatedCourseWorkspaces = refetchPreview?.data?.courseWorkspaces ?? [];
              const workspace = updatedCourseWorkspaces.find((w) => w.id === workspaceId);
              latestThreadId = workspace?.chats?.[0]?.id || null;
            }
            if (latestThreadId) {
              setActiveThreadId(latestThreadId);
              navigation.setParams({ chatId: latestThreadId });
            } else {
              setActiveThreadId(null);
              navigation.setParams({ chatId: undefined });
            }
          }
        } else {
          throw new Error("Failed to delete thread");
        }
      } catch (error) {
        console.error("Error deleting companion chat:", error);
        const errorMessage = error instanceof Error ? error.message : "Failed to delete thread";
        const capturedId = id;
        setError({
          title: "Failed to delete thread",
          message: errorMessage || "An error occurred while deleting the thread. Please try again.",
          onRetry: async () => {
            await handleDeleteThread(capturedId);
          },
          retryButtonText: "Retry",
        });
      }
    },
    [activeThreadId, activeWorkspaceId, deleteCompanionChat, navigation, refetchPreviewChats, setActiveThreadId]
  );

  useResolveChatWorkspace({
    routeChatId,
    generalChats,
    courseWorkspaces,
    threadStateByWorkspaceId,
    setActiveThreadId,
    setActiveWorkspaceId,
    setOpenWorkspaceId,
    onLoadMoreThreads: handleLoadMoreThreads,
    refetchPreviewChats: handleRefetchPreviewChats,
    activeWorkspaceId,
  });

  useEffect(() => {
    const initialMessageWorkspaceId = routeParams?.initialMessage?.workspaceId;

    if (!hasInitializedRef.current && activeWorkspaceId === null && !routeChatId) {
      hasInitializedRef.current = true;
      // Use workspace from initialMessage if available, otherwise default to general
      const workspaceToSet = initialMessageWorkspaceId ?? GENERAL_WORKSPACE_ID;
      setActiveWorkspaceId(workspaceToSet);
      // Open course workspace if specified in initialMessage
      if (initialMessageWorkspaceId && initialMessageWorkspaceId !== GENERAL_WORKSPACE_ID) {
        setOpenWorkspaceId(initialMessageWorkspaceId);
      }
    }
  }, [activeWorkspaceId, routeChatId, routeParams, setActiveWorkspaceId, setOpenWorkspaceId]);

  useEffect(() => {
    if (!currentRouteName || currentRouteName === "Companion Chat") {
      return;
    }

    const workspaceId = ROUTE_TO_WORKSPACE[currentRouteName];
    if (workspaceId && activeWorkspaceId !== workspaceId) {
      setActiveWorkspaceId(workspaceId);
    }
  }, [currentRouteName, activeWorkspaceId, setActiveWorkspaceId]);

  return (
    <CompanionThreadsContext.Provider
      value={{
        activeThreadId,
        setActiveThreadId,
        activeWorkspaceId,
        setActiveWorkspaceId,
        openWorkspaceId,
        setOpenWorkspaceId,
        toggleWorkspaceDrawer,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        generalWorkspaces,
        courseWorkspaces: courseWorkspaceItems,
        todoWorkspaces,
        learningActivitiesWorkspaces,
        settingsWorkspaces,
        threadsByWorkspaceId,
        activeChatThread,
        activeChatThreadLoading,
        isLoadingCourseWorkspaces,
        previewError: previewError ?? undefined,
        refetchPreviewChats: handleRefetchPreviewChats,
        activeChatError: activeChatError ?? undefined,
        refetchActiveChat,
        onWorkspacePress: handleWorkspacePress,
        onThreadSelect: handleSelectThread,
        onThreadDelete: handleDeleteThread,
        onNewThread: handleNewThread,
        onThreadRename: handleRenameThread,
        onThreadLoadMore: handleLoadMoreThreads,
        applyStreamChatRename,
        getThreadName,
        getWorkspaceName,
        error,
        showError,
        hideError,
      }}>
      {children}
    </CompanionThreadsContext.Provider>
  );
};

export const useCompanionThreads = () => {
  const context = useContext(CompanionThreadsContext);
  if (!context) {
    throw new Error("useCompanionThreads must be used within a CompanionThreadsProvider");
  }
  return context;
};

export const useWorkspaceThreads = useCompanionThreads;
